import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key from base64url to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker and check subscription status
  useEffect(() => {
    if (!isSupported) return;
    
    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        
        if (existingSubscription) {
          setSubscription(existingSubscription);
          setIsSubscribed(true);
        }
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };
    
    // Register service worker
    navigator.serviceWorker.register('/sw.js')
      .then(() => {
        console.log('Service Worker registered');
        checkSubscription();
      })
      .catch((err) => {
        console.error('Service Worker registration failed:', err);
      });
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (token) => {
    if (!isSupported || !VAPID_PUBLIC_KEY) {
      setError('Push notifications not supported or not configured');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      
      // Send subscription to server
      const subscriptionJson = pushSubscription.toJSON();
      
      await axios.post(`${API_URL}/api/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscription(pushSubscription);
      setIsSubscribed(true);
      setLoading(false);
      
      console.log('Push notification subscription successful');
      return true;
      
    } catch (err) {
      console.error('Error subscribing to push:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to subscribe');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (token) => {
    if (!subscription) {
      return true;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();
      
      // Notify server
      const subscriptionJson = subscription.toJSON();
      await axios.delete(`${API_URL}/api/push/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        }
      });
      
      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
      
      console.log('Push notification unsubscription successful');
      return true;
      
    } catch (err) {
      console.error('Error unsubscribing from push:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to unsubscribe');
      setLoading(false);
      return false;
    }
  }, [subscription]);

  // Send a test notification
  const sendTestNotification = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/api/push/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to send test');
      setLoading(false);
      return false;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}

export default usePushNotifications;
